import React from "react";
import { motion } from "framer-motion";
import { LayoutDashboard, Megaphone, BarChart3, ShieldCheck, Bell, TrendingUp, ArrowUpRight } from "lucide-react";

const FEATURES = [
  { icon: LayoutDashboard, title: "Batch readiness dashboard", body: "See your whole batch ranked by an objective Wishire Score — who's job-ready, who's at risk — months before drives start." },
  { icon: Megaphone, title: "Campus drives, on autopilot", body: "Post drives with eligibility rules (CGPA, branch, min score). Applicants arrive auto-ranked; shortlist in one place." },
  { icon: TrendingUp, title: "At-risk interventions", body: "Flag low-readiness students and nudge them into mock interviews and skill roadmaps — then watch readiness climb." },
  { icon: ShieldCheck, title: "Verified, trusted data", body: "Verify CGPA and documents so recruiters trust every profile. Integrity-checked AI interviews back the score." },
  { icon: BarChart3, title: "Placement reports", body: "Branch-wise readiness and placement analytics, exportable for NIRF / NAAC — the numbers you report upward, ready." },
  { icon: Bell, title: "Announcements & reminders", body: "Broadcast drive updates and deadlines to your batch in a click. No more WhatsApp-broadcast chaos." },
];

const rise = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] } }),
};

export default function CollegeFeatures() {
  return (
    <section id="for-colleges" className="section bg-stone-950 text-stone-50 wsh-grain relative overflow-hidden">
      <div className="absolute inset-0 opacity-50 pointer-events-none" style={{ background: "radial-gradient(700px 420px at 82% 8%, rgba(224,165,38,.16), transparent 60%)" }} />
      <div className="container-x relative">
        <div className="max-w-2xl">
          <span className="tag" style={{ background: "rgba(255,255,255,.06)", color: "#FCD34D", borderColor: "rgba(255,255,255,.14)" }}>
            For colleges &amp; placement cells
          </span>
          <h2 className="font-display text-4xl md:text-6xl mt-6 leading-[1.02]">
            Run placements on <span className="italic text-amber-300">proof</span>, not spreadsheets.
          </h2>
          <p className="mt-5 text-lg text-stone-300 leading-relaxed">
            Wishire gives your placement cell an objective readiness signal for every student —
            so you can intervene early, run drives that convert, and lift your placement percentage.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              custom={i}
              variants={rise}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 hover:bg-white/[0.07] transition-colors"
            >
              <span className="inline-flex p-2.5 rounded-2xl bg-amber-300/12 text-amber-300"><f.icon size={20} /></span>
              <h3 className="font-display text-xl mt-4">{f.title}</h3>
              <p className="mt-2 text-sm text-stone-300 leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5 rounded-3xl border border-white/10 bg-white/[0.04] p-7">
          <div className="flex-1">
            <p className="font-display text-2xl">Upload your batch. See readiness in minutes.</p>
            <p className="mt-1.5 text-stone-400 text-sm">Bulk-import students, invite your placement team, and go live before your next drive.</p>
          </div>
          <a href="#book-demo" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-amber-300 text-stone-900 font-medium hover:bg-amber-200 transition-colors shrink-0">
            Book a demo <ArrowUpRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
